/**
 * Preview Component for Reels video editor
 * Renders the video preview canvas with overlays
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { useProject, useUI } from '../stores/projectStore';
import { formatDuration } from '../utils/timeUtils';

const Preview = () => {
  const { project } = useProject();
  const { ui, actions } = useUI();
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastClipIdRef = useRef(null);
  const imageRef = useRef(null);
  const loadedImagesRef = useRef({}); // Cache for loaded images

  const [isMuted, setIsMuted] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [imageReady, setImageReady] = useState(false);

  const { settings } = project;
  const { currentTime, isPlaying } = ui;

  // Calculate preview dimensions to fit container while maintaining aspect ratio
  const getPreviewDimensions = useCallback(() => {
    if (!containerRef.current) return { width: 0, height: 0 };

    const container = containerRef.current;
    const containerWidth = container.clientWidth - 32;
    const containerHeight = container.clientHeight - 80;

    const aspectRatio = settings.width / settings.height;
    const containerAspect = containerWidth / containerHeight;

    let width, height;
    if (aspectRatio > containerAspect) {
      width = containerWidth;
      height = containerWidth / aspectRatio;
    } else {
      height = containerHeight;
      width = containerHeight * aspectRatio;
    }

    return { width: width * previewScale, height: height * previewScale };
  }, [settings.width, settings.height, previewScale]);

  // Find the current clip at playhead position (video or image)
  const getCurrentClip = useCallback(() => {
    for (const track of project.tracks) {
      if (track.type !== 'video' || !track.visible) continue;
      for (const clip of track.clips) {
        if (currentTime >= clip.startTime && currentTime < clip.startTime + clip.duration) {
          return clip;
        }
      }
    }
    return null;
  }, [project.tracks, currentTime]);

  // For backwards compatibility
  const getCurrentVideoClip = getCurrentClip;

  // Get media item from library by ID
  const getMediaById = useCallback((mediaId) => {
    return project.mediaLibrary.find(m => m.id === mediaId);
  }, [project.mediaLibrary]);

  // Check if media is an image
  const isImageMedia = useCallback((media) => {
    if (!media) return false;
    return media.type === 'image' ||
           (media.mimeType && media.mimeType.startsWith('image/')) ||
           /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i.test(media.name || '');
  }, []);

  // Load image for image clips
  useEffect(() => {
    const currentClip = getCurrentClip();
    if (!currentClip) {
      setImageReady(false);
      return;
    }

    const media = getMediaById(currentClip.mediaId);
    if (!media || !isImageMedia(media)) {
      setImageReady(false);
      return;
    }

    // Check if already cached
    if (loadedImagesRef.current[media.id]) {
      imageRef.current = loadedImagesRef.current[media.id];
      setImageReady(true);
      return;
    }

    // Load new image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      loadedImagesRef.current[media.id] = img;
      imageRef.current = img;
      setImageReady(true);
      console.log('Image loaded:', media.name, img.width, 'x', img.height);
    };
    img.onerror = (e) => {
      console.error('Image load error:', e);
      setImageReady(false);
    };
    img.src = media.url;
  }, [getCurrentClip, getMediaById, isImageMedia]);

  // Load video source when clip changes (skip if image)
  useEffect(() => {
    const currentClip = getCurrentClip();
    const video = videoRef.current;

    if (!video) return;

    // If no clip at current time, clear the video
    if (!currentClip) {
      if (video.src) {
        video.src = '';
        setVideoReady(false);
      }
      lastClipIdRef.current = null;
      return;
    }

    // Get the media item for this clip
    const media = getMediaById(currentClip.mediaId);

    // Skip if this is an image
    if (media && isImageMedia(media)) {
      if (video.src) {
        video.src = '';
        setVideoReady(false);
      }
      return;
    }

    // If same clip, don't reload
    if (currentClip.id === lastClipIdRef.current) {
      return;
    }

    if (media && media.url) {
      console.log('Loading video source:', media.url);
      setVideoReady(false);
      video.src = media.url;
      video.load();
      lastClipIdRef.current = currentClip.id;
    }
  }, [getCurrentClip, getMediaById, isImageMedia]);

  // Handle video loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      console.log('Video can play');
      setVideoReady(true);
    };

    const handleLoadedData = () => {
      console.log('Video loaded data');
      setVideoReady(true);
    };

    const handleError = (e) => {
      console.error('Video error:', e);
      setVideoReady(false);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
    };
  }, []);

  // Sync video time with timeline
  useEffect(() => {
    const video = videoRef.current;
    const currentClip = getCurrentVideoClip();

    if (!video || !currentClip || !videoReady) return;

    // Calculate the time within the video based on clip's inPoint
    const clipTime = currentTime - currentClip.startTime + (currentClip.inPoint || 0);

    // Only seek if difference is significant and not playing
    if (!isPlaying && Math.abs(video.currentTime - clipTime) > 0.05) {
      video.currentTime = Math.max(0, Math.min(clipTime, video.duration || Infinity));
    }
  }, [currentTime, isPlaying, getCurrentVideoClip, videoReady]);

  // Handle play/pause sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoReady) return;

    if (isPlaying) {
      video.play().catch(e => console.log('Play prevented:', e));
    } else {
      video.pause();
    }
  }, [isPlaying, videoReady]);

  // Render frame to canvas
  const renderFrame = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const image = imageRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = getPreviewDimensions();

    canvas.width = settings.width;
    canvas.height = settings.height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    // Clear canvas with black
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Get current clip
    const currentClip = getCurrentClip();
    const media = currentClip ? getMediaById(currentClip.mediaId) : null;
    const isImage = media && isImageMedia(media);

    // Draw image if this is an image clip
    if (currentClip && isImage && imageReady && image) {
      // Apply effects/filters
      if (currentClip.effects && currentClip.effects.length > 0) {
        const filters = currentClip.effects
          .filter(e => e.type === 'filter' && e.filter)
          .map(e => e.filter)
          .join(' ');
        ctx.filter = filters || 'none';
      }

      // Calculate how to draw the image (cover the canvas)
      const imageAspect = image.naturalWidth / image.naturalHeight;
      const canvasAspect = canvas.width / canvas.height;

      let sx = 0, sy = 0, sw = image.naturalWidth, sh = image.naturalHeight;
      let dx = 0, dy = 0, dw = canvas.width, dh = canvas.height;

      if (imageAspect > canvasAspect) {
        // Image is wider - crop sides
        sw = image.naturalHeight * canvasAspect;
        sx = (image.naturalWidth - sw) / 2;
      } else {
        // Image is taller - crop top/bottom
        sh = image.naturalWidth / canvasAspect;
        sy = (image.naturalHeight - sh) / 2;
      }

      ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh);
      ctx.filter = 'none';
    }
    // Draw video frame if ready
    else if (currentClip && !isImage && video && videoReady && video.readyState >= 2) {
      // Apply effects/filters
      if (currentClip.effects && currentClip.effects.length > 0) {
        const filters = currentClip.effects
          .filter(e => e.type === 'filter' && e.filter)
          .map(e => e.filter)
          .join(' ');
        ctx.filter = filters || 'none';
      }

      // Calculate how to draw the video (cover the canvas)
      const videoAspect = video.videoWidth / video.videoHeight;
      const canvasAspect = canvas.width / canvas.height;

      let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
      let dx = 0, dy = 0, dw = canvas.width, dh = canvas.height;

      if (videoAspect > canvasAspect) {
        // Video is wider - crop sides
        sw = video.videoHeight * canvasAspect;
        sx = (video.videoWidth - sw) / 2;
      } else {
        // Video is taller - crop top/bottom
        sh = video.videoWidth / canvasAspect;
        sy = (video.videoHeight - sh) / 2;
      }

      ctx.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);
      ctx.filter = 'none';
    }

    // Render text overlays
    project.textClips.forEach(textClip => {
      if (currentTime >= textClip.startTime && currentTime < textClip.startTime + textClip.duration) {
        renderTextClip(ctx, textClip, canvas.width, canvas.height);
      }
    });

    // Render shape overlays
    project.shapeClips.forEach(shapeClip => {
      if (currentTime >= shapeClip.startTime && currentTime < shapeClip.startTime + shapeClip.duration) {
        renderShapeClip(ctx, shapeClip, canvas.width, canvas.height);
      }
    });
  }, [currentTime, settings, getPreviewDimensions, getCurrentClip, getMediaById, isImageMedia, project.textClips, project.shapeClips, videoReady, imageReady]);

  // Calculate animation values based on progress
  const getAnimationValues = (animationType, progress, canvasWidth, canvasHeight) => {
    const animDuration = 0.5; // Animation duration in seconds (as fraction of clip)
    const animProgress = Math.min(progress / animDuration, 1);

    // Easing functions
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const easeOutBounce = (t) => {
      const n1 = 7.5625;
      const d1 = 2.75;
      if (t < 1 / d1) return n1 * t * t;
      if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
      if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
      return n1 * (t -= 2.625 / d1) * t + 0.984375;
    };
    const easeOutElastic = (t) => {
      if (t === 0 || t === 1) return t;
      return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
    };

    let opacity = 1;
    let translateX = 0;
    let translateY = 0;
    let scale = 1;
    let rotation = 0;
    let blur = 0;

    switch (animationType) {
      case 'fade-in':
        opacity = easeOutCubic(animProgress);
        break;
      case 'slide-up':
        opacity = easeOutCubic(animProgress);
        translateY = (1 - easeOutCubic(animProgress)) * canvasHeight * 0.3;
        break;
      case 'slide-down':
        opacity = easeOutCubic(animProgress);
        translateY = (1 - easeOutCubic(animProgress)) * -canvasHeight * 0.3;
        break;
      case 'slide-left':
        opacity = easeOutCubic(animProgress);
        translateX = (1 - easeOutCubic(animProgress)) * canvasWidth * 0.3;
        break;
      case 'slide-right':
        opacity = easeOutCubic(animProgress);
        translateX = (1 - easeOutCubic(animProgress)) * -canvasWidth * 0.3;
        break;
      case 'zoom-in':
        opacity = easeOutCubic(animProgress);
        scale = easeOutCubic(animProgress);
        break;
      case 'bounce':
        opacity = Math.min(animProgress * 2, 1);
        scale = easeOutBounce(animProgress);
        break;
      case 'pop':
        opacity = Math.min(animProgress * 2, 1);
        scale = easeOutElastic(animProgress);
        break;
      case 'blur-in':
        opacity = easeOutCubic(animProgress);
        blur = (1 - easeOutCubic(animProgress)) * 20;
        break;
      case 'flip':
        opacity = animProgress > 0.5 ? 1 : 0;
        scale = Math.abs(Math.cos(animProgress * Math.PI));
        break;
      case 'rotate':
        opacity = easeOutCubic(animProgress);
        rotation = (1 - easeOutCubic(animProgress)) * -360;
        scale = easeOutCubic(animProgress);
        break;
      case 'wave':
        opacity = easeOutCubic(animProgress);
        translateY = Math.sin(animProgress * Math.PI * 4) * (1 - animProgress) * 20;
        break;
      case 'typewriter':
        // Typewriter is handled separately in the text rendering
        opacity = 1;
        break;
      case 'none':
      default:
        // No animation
        break;
    }

    return { opacity, translateX, translateY, scale, rotation, blur };
  };

  // Render text clip on canvas
  const renderTextClip = (ctx, clip, canvasWidth, canvasHeight) => {
    const baseX = (clip.position?.x || 0.5) * canvasWidth;
    const baseY = (clip.position?.y || 0.5) * canvasHeight;

    // Calculate animation progress (time within the clip)
    const clipProgress = (currentTime - clip.startTime) / clip.duration;
    const entranceAnim = clip.animation?.entrance || 'none';

    // Get animation values
    const anim = getAnimationValues(entranceAnim, clipProgress, canvasWidth, canvasHeight);

    // Apply animation transforms
    const x = baseX + anim.translateX;
    const y = baseY + anim.translateY;

    ctx.save();
    ctx.globalAlpha = anim.opacity * (clip.opacity || 1);
    ctx.translate(x, y);

    // Apply scale
    if (anim.scale !== 1) {
      ctx.scale(anim.scale, anim.scale);
    }

    // Apply rotation (animation + clip rotation)
    const totalRotation = (clip.rotation || 0) + anim.rotation;
    if (totalRotation) {
      ctx.rotate((totalRotation * Math.PI) / 180);
    }

    // Apply blur filter if needed
    if (anim.blur > 0) {
      ctx.filter = `blur(${anim.blur}px)`;
    }

    // Handle badge elements (styled text boxes)
    if (clip.backgroundColor) {
      const fontSize = (clip.fontSize || 24) * (canvasWidth / 1080);
      ctx.font = `${clip.fontWeight || 'bold'} ${fontSize}px ${clip.fontFamily || 'Arial'}`;
      const textMetrics = ctx.measureText(clip.text || '');
      const padding = (clip.padding || 12) * (canvasWidth / 1080);
      const textWidth = textMetrics.width;
      const textHeight = fontSize;
      const boxWidth = textWidth + padding * 2;
      const boxHeight = textHeight + padding * 1.5;
      const radius = (clip.borderRadius || 6) * (canvasWidth / 1080);

      // Draw rounded rectangle background
      ctx.fillStyle = clip.backgroundColor;
      ctx.beginPath();
      ctx.roundRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, radius);
      ctx.fill();

      // Draw text
      ctx.fillStyle = clip.color || '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(clip.text || '', 0, 0);
    }
    // Handle sticker elements (icons with labels)
    else if (clip.isSticker && clip.svgPath) {
      const iconSize = (clip.iconSize || 80) * (canvasWidth / 1080);

      // Draw icon background circle
      ctx.fillStyle = clip.iconBgColor || 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      ctx.arc(0, 0, iconSize / 2 + 10, 0, Math.PI * 2);
      ctx.fill();

      // Draw the SVG icon
      ctx.save();
      ctx.translate(-iconSize / 2, -iconSize / 2);
      ctx.scale(iconSize / 24, iconSize / 24);
      ctx.strokeStyle = clip.color || '#ffffff';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke(new Path2D(clip.svgPath));
      ctx.restore();
    }
    // Handle emoji elements
    else if (clip.isEmoji) {
      const fontSize = (clip.fontSize || 72) * (canvasWidth / 1080);
      ctx.font = `${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(clip.text || '', 0, 0);
    }
    // Regular text (including stickers without svgPath)
    else {
      const fontSize = (clip.fontSize || 48) * (canvasWidth / 1080);
      ctx.font = `${clip.fontWeight || 'bold'} ${clip.fontStyle || 'normal'} ${fontSize}px ${clip.fontFamily || 'Arial'}`.trim().replace(/\s+/g, ' ');
      ctx.textAlign = clip.textAlign || 'center';
      ctx.textBaseline = 'middle';

      // Add shadow for better visibility
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;

      // Handle typewriter animation
      let displayText = clip.text || '';
      if (entranceAnim === 'typewriter') {
        const animDuration = 0.7; // Typewriter takes longer
        const charProgress = Math.min(clipProgress / animDuration, 1);
        const numChars = Math.floor(charProgress * displayText.length);
        displayText = displayText.substring(0, numChars);
      }

      if (clip.strokeColor && clip.strokeWidth) {
        ctx.strokeStyle = clip.strokeColor;
        ctx.lineWidth = clip.strokeWidth * 2;
        ctx.lineJoin = 'round';
        ctx.strokeText(displayText, 0, 0);
      }

      ctx.fillStyle = clip.color || '#ffffff';
      ctx.fillText(displayText, 0, 0);
    }

    ctx.filter = 'none';
    ctx.restore();
  };

  // Render shape clip on canvas
  const renderShapeClip = (ctx, clip, canvasWidth, canvasHeight) => {
    const x = (clip.position?.x || 0.5) * canvasWidth;
    const y = (clip.position?.y || 0.5) * canvasHeight;
    const width = (clip.width || 100) * (canvasWidth / 1080);
    const height = (clip.height || 100) * (canvasWidth / 1080);

    ctx.save();
    ctx.translate(x, y);

    if (clip.rotation) {
      ctx.rotate((clip.rotation * Math.PI) / 180);
    }

    ctx.fillStyle = clip.fillColor || '#ffffff';
    ctx.strokeStyle = clip.strokeColor || 'transparent';
    ctx.lineWidth = clip.strokeWidth || 0;

    switch (clip.shape) {
      case 'rectangle':
        ctx.fillRect(-width / 2, -height / 2, width, height);
        if (clip.strokeWidth) ctx.strokeRect(-width / 2, -height / 2, width, height);
        break;
      case 'circle':
        ctx.beginPath();
        ctx.arc(0, 0, width / 2, 0, Math.PI * 2);
        ctx.fill();
        if (clip.strokeWidth) ctx.stroke();
        break;
      case 'triangle':
        ctx.beginPath();
        ctx.moveTo(0, -height / 2);
        ctx.lineTo(width / 2, height / 2);
        ctx.lineTo(-width / 2, height / 2);
        ctx.closePath();
        ctx.fill();
        if (clip.strokeWidth) ctx.stroke();
        break;
      default:
        break;
    }

    ctx.restore();
  };

  // Animation/playback loop
  useEffect(() => {
    let lastTime = performance.now();

    const tick = (now) => {
      if (isPlaying) {
        const delta = (now - lastTime) / 1000;
        lastTime = now;

        const newTime = currentTime + delta;
        const duration = getDuration();

        if (newTime >= duration) {
          actions.setCurrentTime(0);
          actions.setPlaying(false);
        } else {
          actions.setCurrentTime(newTime);
        }
      } else {
        lastTime = now;
      }

      renderFrame();
      animationFrameRef.current = requestAnimationFrame(tick);
    };

    animationFrameRef.current = requestAnimationFrame(tick);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, currentTime, renderFrame, actions]);

  // Get project duration
  const getDuration = useCallback(() => {
    let maxTime = 0;
    project.tracks.forEach(track => {
      track.clips.forEach(clip => {
        const endTime = clip.startTime + clip.duration;
        if (endTime > maxTime) maxTime = endTime;
      });
    });
    project.textClips.forEach(clip => {
      const endTime = clip.startTime + clip.duration;
      if (endTime > maxTime) maxTime = endTime;
    });
    return maxTime || 10;
  }, [project]);

  // Toggle playback
  const togglePlay = () => {
    actions.setPlaying(!isPlaying);
  };

  // Reset to beginning
  const resetPlayback = () => {
    actions.setCurrentTime(0);
    actions.setPlaying(false);
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Zoom controls
  const zoomIn = () => setPreviewScale(Math.min(previewScale + 0.25, 2));
  const zoomOut = () => setPreviewScale(Math.max(previewScale - 0.25, 0.5));

  const duration = getDuration();
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-[#0b1118]">
      {/* Preview Canvas Area */}
      <div className="relative flex-1 flex items-center justify-center p-6 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(20, 184, 166, 0.12), transparent 45%), radial-gradient(circle at 80% 30%, rgba(245, 158, 11, 0.12), transparent 40%)'
          }}
        />
        <div className="relative">
          <div className="rounded-2xl border border-slate-800/80 bg-black/80 p-3 shadow-[0_24px_60px_rgba(0,0,0,0.35)]">
            {/* Canvas */}
            <canvas
              ref={canvasRef}
              className="bg-black rounded-xl shadow-2xl"
              onClick={togglePlay}
            />
          </div>

          {/* Hidden video element for source */}
          <video
            ref={videoRef}
            className="hidden"
            muted={isMuted}
            playsInline
            crossOrigin="anonymous"
          />

          {/* Play/Pause overlay */}
          {!isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlay}
            >
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Playback Controls */}
      <div className="px-4 pb-4">
        {/* Progress bar */}
        <div className="mb-3">
          <div
            className="h-1.5 bg-slate-800 rounded-full cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              actions.setCurrentTime(percent * duration);
            }}
          >
            <div
              className="h-full bg-teal-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Control buttons */}
        <div className="flex items-center justify-between bg-slate-900/80 border border-slate-800/80 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-2">
            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="p-2 rounded-lg hover:bg-slate-800 text-white transition-colors"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>

            {/* Reset */}
            <button
              onClick={resetPlayback}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Volume */}
            <button
              onClick={toggleMute}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>

            {/* Time display */}
            <span className="text-sm text-slate-400 ml-2">
              {formatDuration(currentTime)} / {formatDuration(duration)}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <button
              onClick={zoomOut}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              disabled={previewScale <= 0.5}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500 w-12 text-center">
              {Math.round(previewScale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
              disabled={previewScale >= 2}
            >
              <ZoomIn className="w-4 h-4" />
            </button>

            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors ml-2"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview;
